// MarkdownReader — full-screen modal that fetches a .md attachment and renders
// it as formatted markdown in-app (instead of downloading / showing raw text).
// Mirrors MediaViewer's overlay + close interactions (backdrop, ×, Escape,
// body-scroll lock). Used by MessageAttachments for the "md" kind.
//
// Fetches the raw markdown text on open via fetch(); ClickUp attachment URLs
// are GET-fetchable. On a fetch/CORS failure it degrades to an "Open in new
// tab" chip so the user can still reach the file. Rendering is done by the safe
// renderMarkdown() helper (React nodes, no dangerouslySetInnerHTML).
import React, { useEffect, useState } from "react";
import { renderMarkdown } from "./renderMarkdown";

export default function MarkdownReader({ attachment, onClose }) {
  const att = attachment || {};
  const url = att.url;
  const name = att.name || "document.md";
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [text, setText] = useState("");

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.text();
        if (!cancelled) { setText(body); setStatus("ready"); }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    // Escape closes; lock body scroll while open.
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelled = true;
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [url, onClose]);

  if (!url) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      data-testid="markdown-reader"
    >
      <div className="absolute inset-0 bg-black/80" onClick={onClose} aria-hidden="true" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center gap-3 px-4 py-3 text-white">
        <svg className="h-5 w-5 shrink-0 text-blue-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <span className="min-w-0 truncate text-sm font-medium">{name}</span>
        {status === "error" && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-md border border-white/30 px-2.5 py-1 text-xs font-medium hover:bg-white/10"
            data-testid="markdown-reader-open-tab"
          >
            Open in new tab
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Close reader"
          data-testid="markdown-reader-close"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl bg-white px-6 py-6 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
          {status === "loading" && (
            <div className="space-y-2" data-testid="markdown-reader-loading">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-800" style={{ width: `${60 + ((i * 7) % 35)}%` }} />
              ))}
            </div>
          )}
          {status === "error" && (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">Couldn't load this document inline.</p>
              <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-block rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800">
                Open {name} in a new tab
              </a>
            </div>
          )}
          {status === "ready" && (
            <div data-testid="markdown-reader-content">{renderMarkdown(text)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
